import { useParams, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { ShoppingCart } from "lucide-react";
import { useLocale } from "@/hooks/use-locale";
import { trackCTAClick } from "@/lib/analytics";
import JsonLd from "@/components/JsonLd";
import FaqGroupsSection, { toFaqGroup } from "@/components/FaqGroupsSection";
import { productSchema, breadcrumbSchema } from "@/lib/seo-schemas";


type ProductData = {
  name: string;
  /** Optional shorter name used for <title> tags (kept ≤ 60 chars after suffix). */
  shortName?: string;
  description: string;
  /** Optional shorter description for <meta name="description"> (≤ 160 chars). */
  metaDescription?: string;
  features: string[];
  image: string;
  images?: string[];
  amazonUrl: string;
  sku: string;
  price: number;
  category?: string;
};


const productData: Record<string, ProductData> = {
  "gift-set": {
    name: "Queebi Gift Set",
    description: "A beautifully curated gift set featuring Queebi characters, perfect for newborns and toddlers. Includes a PaciBuddy, comforter, and a keepsake storage box.",
    features: ["PaciBuddy with detachable pacifier clip", "Soft comforter blanket", "Premium storage box", "Perfect for baby showers and birthdays"],
    image: "/images/products/gift-set.jpg",
    amazonUrl: "https://www.amazon.com/s?k=queebi",
    sku: "QB-GIFT-001",
    price: 59.99,
    category: "Baby & Toddler > Baby Gift Sets",
  },
  "comforter": {
    name: "Queebi Comforter",
    description: "A soft, soothing comforter designed to provide warmth and emotional security. Each comforter features a beloved Queebi character.",
    features: ["Ultra-soft fabric safe for sensitive skin", "Queebi character design", "Machine washable", "Perfect size for little hands"],
    image: "/images/products/comforter.jpg",
    amazonUrl: "https://www.amazon.com/s?k=queebi+comforter",
    sku: "QB-COMF-001",
    price: 24.99,
    category: "Baby & Toddler > Comforters & Loveys",
  },
  "pacibuddy": {
    name: "Queebi PaciBuddy — Plush Pacifier Holder with Stuffed Animal for Newborns",
    shortName: "PaciBuddy — Plush Pacifier Holder",
    description: "The Queebi PaciBuddy is a plush pacifier holder with a stuffed animal attached — a soft, huggable paci buddy that keeps baby's pacifier close while giving them a comforting friend to hold. A gender-neutral alternative to clip-on pacifier holders, designed as a thoughtful newborn or baby shower gift.",
    metaDescription: "PaciBuddy is a plush pacifier holder with a stuffed animal attached — a gender-neutral WubbaNub alternative, perfect newborn or baby shower gift.",
    features: ["Detachable pacifier with universal adapter — works with most pacifier brands (Nuk, MAM, Avent, Philips, Soothie, and more)", "Plush pacifier holder doubles as a soft stuffed animal companion", "BPA-free, baby-safe materials suitable from newborn", "Available in all 6 Queebi animal characters — gender-neutral designs", "Perfect newborn baby gift, baby shower gift, or baby registry must-have", "A cuddly alternative to WubbaNub-style and Nookums-style paci plush toys"],
    image: "/images/products/pacibuddy.jpg",
    amazonUrl: "https://www.amazon.com/s?k=queebi+pacibuddy",
    sku: "QB-PACI-001",
    price: 29.99,
    category: "Baby & Toddler > Pacifiers & Teethers > Pacifier Holders",
  },

  "swaddle": {
    name: "Queebi Swaddle",
    description: "A gentle, breathable swaddle wrap featuring Queebi Land illustrations. Designed to make babies feel safe and cozy.",
    features: ["100% organic cotton", "Breathable and lightweight", "Queebi Land character prints", "Multi-use: swaddle, blanket, nursing cover"],
    image: "/images/products/swaddle.jpg",
    amazonUrl: "https://www.amazon.com/s?k=queebi+swaddle",
    sku: "QB-SWAD-001",
    price: 19.99,
    category: "Baby & Toddler > Swaddling Blankets",
  },
  "storage-box": {
    name: "Queebi Storage Box",
    description: "A premium keepsake storage box for baby's treasured items. Beautifully illustrated with Queebi characters.",
    features: ["Sturdy premium construction", "Queebi character illustrations", "Perfect for keepsakes and memories", "Reusable and decorative"],
    image: "/images/products/storage-box.jpg",
    amazonUrl: "https://www.amazon.com/s?k=queebi+storage+box",
    sku: "QB-STOR-001",
    price: 34.99,
    category: "Baby & Toddler > Nursery Décor > Storage",
  },
};

const ProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { prefix } = useLocale();
  const product = slug ? productData[slug] : undefined;

  if (!product) {
    return <Navigate to={`${prefix}/queebi-products`} replace />;
  }

  const isPaciBuddy = slug === "pacibuddy";
  const pacibuddyKeywords =
    "Queebi PaciBuddy, plush pacifier holder, pacifier with stuffed animal, paci buddy, paci plush, stuffed animal pacifier, detachable pacifier holder, newborn pacifier with stuffed animal, wubbanub alternative, nookums alternative, nookums paci plushies alternative, baby shower gift, newborn baby gift, gender neutral baby gift, baby registry must haves, baby binky";

  const animalAnchors: { id: string; label: string; blurb: string }[] = [
    { id: "lion-pacifier-holder", label: "Lion pacifier holder", blurb: "Liam the Lion PaciBuddy — a brave little lion plush pacifier holder for newborns who love a bold cuddle buddy." },
    { id: "fox-pacifier-holder", label: "Fox pacifier holder", blurb: "Fabio the Fox PaciBuddy — a curious fox stuffed animal pacifier holder, perfect as a baby shower gift." },
    { id: "koala-pacifier-holder", label: "Koala pacifier holder", blurb: "Kaleb the Koala PaciBuddy — a gentle koala paci buddy that soothes babies with soft, huggable texture." },
    { id: "crocodile-pacifier-holder", label: "Crocodile pacifier holder", blurb: "Clyde the Crocodile PaciBuddy — a calm crocodile plush pacifier holder, a great gender-neutral newborn gift." },
    { id: "deer-pacifier-holder", label: "Deer pacifier holder", blurb: "Dana the Deer PaciBuddy — a kind deer stuffed animal pacifier holder for tender, quiet little ones." },
    { id: "crab-pacifier-holder", label: "Crab pacifier holder", blurb: "Casper the Crab PaciBuddy — a cheerful crab paci plush companion that brings joy to fussy moments." },
  ];

  const pacibuddyFaqGroup = isPaciBuddy
    ? toFaqGroup("pacifierBuddy", t("faq.groups.pacifierBuddy", { returnObjects: true }))
    : null;
  const pacibuddyGroups = pacibuddyFaqGroup ? [pacibuddyFaqGroup] : [];

  return (
    <>
      <PageHead
        title={`${product.shortName || product.name} — Queebi`}
        description={product.metaDescription || product.description}

        canonical={`https://queebi.com/queebi-products/${slug}`}
        image={`/images/og/products/${slug}-1200x630.jpg`}
        imageAlt={`${product.name} — Queebi baby & toddler product`}
        keywords={
          isPaciBuddy
            ? pacibuddyKeywords
            : `Queebi, ${product.name}, baby gift, newborn gift ideas, baby shower gift, nursery decor, toddler toy, emotional learning toy`
        }
      />

      <JsonLd
        data={[
          productSchema({
            name: product.name,
            description: product.description,
            url: `https://queebi.com/queebi-products/${slug}`,
            sku: product.sku,
            images: [
              product.image,
              `/images/og/products/${slug}-1200x630.jpg`,
            ],
            price: product.price,
            offerUrl: product.amazonUrl,
            category: product.category,
            ...(isPaciBuddy && {
              audience: { suggestedMinAge: 0, audienceType: "Newborn, Infant" },
              additionalProperty: [
                { name: "Pacifier adapter", value: "Universal — fits most pacifier brands" },
                { name: "Materials", value: "BPA-free, baby-safe plush" },
                { name: "Design", value: "Gender-neutral, 6 animal characters" },
              ],
            }),
          }),
          breadcrumbSchema([
            { name: "Home", url: "https://queebi.com/" },
            { name: "Products", url: "https://queebi.com/queebi-products" },
            { name: product.name, url: `https://queebi.com/queebi-products/${slug}` },
          ]),
        ]}
      />


      <section className="bg-background py-20 md:py-32">
        <div className="container">
          <div className="flex flex-col md:flex-row items-start gap-12 max-w-5xl mx-auto">
            <div className="flex-1">
              <img src={product.image} alt={product.name} className="w-full rounded-2xl" width={600} height={450} loading="lazy" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-bold font-heading mb-6">{product.name}</h1>
              <p className="text-muted-foreground text-lg mb-8">{product.description}</p>
              <ul className="space-y-3 mb-10">
                {product.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-base">
                    <span className="text-accent mt-0.5">✦</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button asChild size="lg" className="rounded-full bg-primary text-primary-foreground font-bold transition-transform duration-200 hover:scale-105">
                <a href={product.amazonUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick(`product_shop_${slug}`)}>
                  <ShoppingCart className="mr-2 h-5 w-5" /> {t("productDetail.shopNow")}
                </a>
              </Button>
              <div className="mt-6">
                <Link to={`${prefix}/queebi-products`} className="text-base text-muted-foreground hover:text-foreground underline">
                  {t("productDetail.backToAll")}
                </Link>
              </div>
            </div>
          </div>

          {isPaciBuddy && (
            <div className="max-w-5xl mx-auto mt-20">
              <h2 className="text-2xl md:text-3xl font-bold font-heading mb-4">Plush Pacifier Holders by Queebi Character</h2>
              <p className="text-muted-foreground mb-8">
                Every Queebi PaciBuddy comes as one of six lovable animal characters — pick the plush pacifier holder that matches your little one's spirit. Each is a gender-neutral newborn gift and a soft alternative to WubbaNub or Nookums-style paci plush toys.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {animalAnchors.map((a) => (
                  <li key={a.id} id={a.id} className="scroll-mt-24 rounded-xl bg-card p-5">
                    <h3 className="font-bold text-lg mb-1">{a.label}</h3>
                    <p className="text-sm text-foreground/80">{a.blurb}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {isPaciBuddy && pacibuddyGroups.length > 0 && (
        <FaqGroupsSection title="PaciBuddy — Questions Parents Ask" groups={pacibuddyGroups} />
      )}
    </>
  );
};

export default ProductDetail;
